import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Award, Plus, LogIn, Clock, ArrowRight } from "lucide-react";

const CAT_LABEL = {
  training: "Training",
  conference: "Conference",
  teaching: "Teaching",
  publication: "Publication",
  mentoring: "Mentoring",
  self_study: "Self-study",
};

const REQUIRED_HOURS = {
  CSCE: 10, CSP: 15, CSL: 20, CCSO: 25, PGDCSL: 25,
};

export default function CPDLogger({ designationCode }) {
  const { user } = useAuth();
  const code = (designationCode || "").toUpperCase();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    activity: "",
    activity_date: new Date().toISOString().slice(0, 10),
    hours: 1,
    category: "training",
    notes: "",
  });

  useEffect(() => {
    if (!user) return;
    api.get("/cpd/summary").then((r) => setSummary(r.data)).catch(() => setSummary(null));
  }, [user]);

  const cycle = useMemo(() => {
    if (!summary) return null;
    return (summary.cycles || []).find((c) => c.designation_code === code) || null;
  }, [summary, code]);

  if (!code || !REQUIRED_HOURS[code]) return null;

  // Not logged in
  if (!user) {
    return (
      <section className="mt-10 rounded-2xl border border-[var(--border-warm)] bg-white p-5 flex flex-wrap items-center gap-3" data-testid="cpd-logger-signedout">
        <div className="w-10 h-10 rounded-xl bg-[var(--muted-surface)] grid place-items-center text-[var(--gold-deep)]">
          <Award size={18}/>
        </div>
        <div className="flex-1 min-w-[14rem]">
          <div className="eyebrow">Log CPD against your {code}</div>
          <div className="font-semibold text-sm text-[var(--navy)]">Log in to record CPD hours directly from this page.</div>
          <div className="text-xs text-zinc-600 mt-0.5">Active standing requires {REQUIRED_HOURS[code]} CPD hours per 12-month cycle.</div>
        </div>
        <Link to="/login" className="btn-terracotta inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold" data-testid="cpd-login-btn">
          <LogIn size={13}/> Log in
        </Link>
      </section>
    );
  }

  // Logged in but doesn't hold the designation
  if (!cycle) {
    return (
      <section className="mt-10 rounded-2xl border border-[var(--border-warm)] bg-white p-5 flex flex-wrap items-center gap-3" data-testid="cpd-logger-nocert">
        <div className="w-10 h-10 rounded-xl bg-[var(--muted-surface)] grid place-items-center text-[var(--gold-deep)]">
          <Award size={18}/>
        </div>
        <div className="flex-1 min-w-[14rem]">
          <div className="eyebrow">CPD logging</div>
          <div className="font-semibold text-sm text-[var(--navy)]">Complete your {code} to unlock CPD logging here.</div>
          <div className="text-xs text-zinc-600 mt-0.5">Once your designation is awarded, you can log the {REQUIRED_HOURS[code]} CPD hours per cycle directly from this page.</div>
        </div>
        <Link to="/cpd" className="chip inline-flex items-center gap-1.5" data-testid="cpd-dashboard-link">
          Open CPD dashboard <ArrowRight size={12}/>
        </Link>
      </section>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.activity.trim()) return toast.error("Please describe the activity");
    setLoading(true);
    try {
      await api.post("/cpd/entries", {
        designation_code: code,
        activity: form.activity,
        activity_date: new Date(form.activity_date).toISOString(),
        hours: Number(form.hours),
        category: form.category,
        notes: form.notes,
      });
      toast.success(`${form.hours}h logged towards ${code}`);
      // reload summary to refresh progress
      const s = await api.get("/cpd/summary");
      setSummary(s.data);
      setForm((f) => ({ ...f, activity: "", notes: "", hours: 1 }));
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const pct = Math.min(100, Math.round((cycle.logged_hours / cycle.required_hours) * 100));
  const barColor = cycle.remaining_hours <= 0 ? "bg-emerald-500" : cycle.days_remaining < 30 ? "bg-amber-500" : "bg-[var(--gold-deep)]";

  return (
    <section className="mt-10 rounded-2xl border border-[var(--border-warm)] bg-white overflow-hidden" data-testid="cpd-logger">
      <div className="p-5 flex flex-wrap items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-[var(--muted-surface)] grid place-items-center text-[var(--gold-deep)] shrink-0">
          <Award size={18}/>
        </div>
        <div className="flex-1 min-w-[14rem]">
          <div className="eyebrow">{code} · Current CPD cycle</div>
          <div className="font-display text-xl text-[var(--navy)] leading-tight">
            {cycle.logged_hours}h / {cycle.required_hours}h logged
          </div>
          <div className="text-xs text-zinc-600 mt-1">
            {cycle.remaining_hours > 0
              ? `${cycle.remaining_hours}h still needed · ${cycle.days_remaining} days left in this cycle`
              : "Target met - great job staying current!"}
          </div>
          <div className="mt-2 h-1.5 w-full rounded-full bg-[var(--muted-surface)] overflow-hidden">
            <div className={`${barColor} h-full transition-all`} style={{ width: `${pct}%` }} data-testid="cpd-progress-bar"/>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={() => setOpen((v) => !v)}
            className="btn-terracotta inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold"
            data-testid="cpd-toggle-form-btn"
          >
            <Plus size={13}/> {open ? "Hide form" : "Log CPD"}
          </button>
          <Link to="/cpd" className="chip inline-flex items-center gap-1.5" data-testid="cpd-dashboard-link">
            Dashboard <ArrowRight size={12}/>
          </Link>
        </div>
      </div>
      {open && (
        <form onSubmit={submit} className="border-t border-[var(--border-warm)] bg-[var(--muted-surface)]/40 p-5 grid gap-3 sm:grid-cols-6" data-testid="cpd-inline-form">
          <label className="sm:col-span-3 text-xs font-semibold text-[var(--navy)]">
            Activity
            <input
              className="mt-1 w-full rounded-xl border border-[var(--border-warm)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--navy)]"
              placeholder="e.g. Attended ENISA converged risk webinar"
              value={form.activity}
              onChange={(e) => setForm({ ...form, activity: e.target.value })}
              required
              data-testid="cpd-field-activity"
            />
          </label>
          <label className="sm:col-span-1 text-xs font-semibold text-[var(--navy)]">
            Date
            <input
              type="date"
              className="mt-1 w-full rounded-xl border border-[var(--border-warm)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--navy)]"
              value={form.activity_date}
              onChange={(e) => setForm({ ...form, activity_date: e.target.value })}
              required
              data-testid="cpd-field-date"
            />
          </label>
          <label className="sm:col-span-1 text-xs font-semibold text-[var(--navy)]">
            Hours
            <input
              type="number" step="0.25" min="0.25" max="100"
              className="mt-1 w-full rounded-xl border border-[var(--border-warm)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--navy)]"
              value={form.hours}
              onChange={(e) => setForm({ ...form, hours: e.target.value })}
              required
              data-testid="cpd-field-hours"
            />
          </label>
          <label className="sm:col-span-1 text-xs font-semibold text-[var(--navy)]">
            Category
            <select
              className="mt-1 w-full rounded-xl border border-[var(--border-warm)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--navy)]"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="cpd-field-category"
            >
              {Object.entries(CAT_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </label>
          <label className="sm:col-span-6 text-xs font-semibold text-[var(--navy)]">
            Notes (optional)
            <input
              className="mt-1 w-full rounded-xl border border-[var(--border-warm)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--navy)]"
              placeholder="Add a short note or evidence reference"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="cpd-field-notes"
            />
          </label>
          <div className="sm:col-span-6 flex justify-end gap-2">
            <button type="button" className="chip" onClick={() => setOpen(false)} data-testid="cpd-cancel-btn">Cancel</button>
            <button
              type="submit"
              disabled={loading}
              className="btn-terracotta inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold disabled:opacity-70"
              data-testid="cpd-submit-btn"
            >
              <Clock size={13}/> {loading ? "Saving…" : "Save entry"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
